// Required imports
import React, { useEffect, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  ElementContainer,
  ColumnContainer,
  Card,
} from "../../../components/containers/styledComponents";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import { createCenteredValueLayer } from "./CenteredText";
import { generatePaletteColors } from "../../../utils/graphUtils";
import { darkGreen, graphPaletteV2 } from "../../../styles";
import {
  DivergentVoting,
  StewardshipFilterParam,
} from "../../../models/stewardshipModels";
import { usePost } from "../../../hooks/usePost";
import { formatNumber } from "../../../utils/formattingUtils";
import { ExportButton } from "../../../components/buttons/ExportButton";
import { VOTES_AGAINST_MANAGEMENT_EXPORT_CONFIG } from "../constants";
import {
  VOTING_PIE_CARD_HEIGHT,
  VOTING_PIE_INNER_RADIUS,
  VOTING_PIE_MARGIN,
  VotingPieChartContainer,
  VotingPieEmpty,
  VotingPieLegendContainer,
} from "./VotingPieLayout";

// The main component that renders the ResponsivePie chart
export const VotingAgainstManagementPie: React.FC<{
  filters: StewardshipFilterParam;
}> = ({ filters }) => {
  const [pieData, setPieData] = useState<PieDatum[]>([]);
  const {
    data,
    isLoading,
    sendData: getDivergentVoting,
  } = usePost<DivergentVoting[], StewardshipFilterParam>();
  const colors = generatePaletteColors(pieData, graphPaletteV2);

  useEffect(() => {
    getDivergentVoting("GET_VOTING_AGAINST_MANAGEMENT", filters);
  }, [filters]);

  useEffect(() => {
    if (data) {
      const sortedData: DivergentVoting[] = data.sort(
        (a, b) => b.divergent_count - a.divergent_count
      );
      const formattedData = sortedData.map((item, index) => ({
        id: item.proposal_subcategory,
        label: item.proposal_subcategory,
        value: formatNumber(item.divergent_count),
        color: colors[index % colors.length],
      }));
      setPieData(formattedData);
    }
  }, [data]);

  // Transforming data into a format that can be used by Nivo Pie
  const totalVotes: string = data
    ? formatNumber(
        (data || []).reduce((sum, data) => sum + data.divergent_count, 0)
      )
    : "";
  // eslint-disable-next-line
  const customLayers: any = [
    createCenteredValueLayer(totalVotes, true),
    createCenteredValueLayer(data ? "votes" : "", false),
  ];

  const layersConfig = layers.concat(customLayers);

  return (
    <Card
      h={VOTING_PIE_CARD_HEIGHT}
      isLoading={isLoading}
      style={{ display: "flex", flexDirection: "column" }}
    >
      <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
        Resolution instructed against management
      </CompassParagraph>
      {pieData.length > 0 ? (
        <ElementContainer ai="flex-start">
          <VotingPieChartContainer>
            <ResponsivePie
              theme={theme}
              data={pieData}
              margin={VOTING_PIE_MARGIN}
              innerRadius={VOTING_PIE_INNER_RADIUS}
              layers={layersConfig as any} //eslint-disable-line
              activeOuterRadiusOffset={4}
              borderWidth={1}
              colors={colors}
              borderColor={{ from: "color", modifiers: [["darker", 0]] }}
              arcLabelsSkipAngle={360}
              arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
              enableArcLinkLabels={false}
              defs={definitions}
              fill={fills}
              legends={[]}
            />
          </VotingPieChartContainer>
          <VotingPieLegendContainer>
            <ColumnContainer
              mt={1}
              jc={pieData.length > 8 ? "flex-start" : "center"}
              ai="flex-start"
              ml={0}
            >
              {pieData.map((item, index) => (
                <ElementContainer key={index} mb={5}>
                  <LegendCircle color={colors[index]} />
                  <CompassParagraph color="#8B909A" fontSize={12}>
                    {item.label}: {item.value}
                  </CompassParagraph>
                </ElementContainer>
              ))}
            </ColumnContainer>
          </VotingPieLegendContainer>
        </ElementContainer>
      ) : (
        !isLoading && <VotingPieEmpty>No voting data</VotingPieEmpty>
      )}
      {!isLoading && pieData.length > 0 && (
        <ExportButton
          data={pieData}
          {...VOTES_AGAINST_MANAGEMENT_EXPORT_CONFIG}
        />
      )}
    </Card>
  );
};

// Configurations for the Nivo Pie chart
const theme = {
  legends: { text: { fontFamily: "Apercu", color: "greyText" } },
};

const layers = ["arcs", "arcLinkLabels", "legends"];

const definitions = [
  {
    id: "dots",
    type: "patternDots",
    background: "inherit",
    color: "rgba(255, 255, 255, 0.3)",
    size: 4,
    padding: 1,
    stagger: true,
  },
  {
    id: "lines",
    type: "patternLines",
    background: "inherit",
    color: "rgba(255, 255, 255, 0.3)",
    rotation: -45,
    lineWidth: 6,
    spacing: 10,
  },
];

const fills = [
  { match: { id: "dots" }, id: "dots" },
  { match: { id: "lines" }, id: "lines" },
];

type PieDatum = {
  id: string | number;
  value: string;
  label?: string;
  color?: string;
};
